import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShieldCheck, Truck, RotateCcw, Lock, Loader2, Tag, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { useProducts } from '../context/ProductsContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => n.toFixed(2).replace('.', ',');
const SHIP_THRESHOLD = 25;
const SHIP_COST = 4.90;
const BUNDLE_PRICE = 20.99;

export default function Checkout() {
  const { lines, subtotal, startCheckout, discountCode, setDiscountCode } = useCart();
  const { products, loading: productsLoading } = useProducts();
  const { vacationMode, returnDate } = useSiteSettings();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(false);

  // Discount code
  const [codeInput, setCodeInput] = useState(discountCode || '');
  const [appliedPct, setAppliedPct] = useState(0);
  const [applyingCode, setApplyingCode] = useState(false);

  // Bundle mode: /checkout?bundle=id1,id2,id3
  const bundleIds = (params.get('bundle') || '').split(',').filter(Boolean);
  const isBundle = bundleIds.length > 0;
  const bundleProducts = isBundle ? bundleIds.map(id => products.find(p => p.id === id)).filter(Boolean) : [];

  const displayLines = isBundle
    ? bundleProducts.map(p => ({ product_id: p.id, quantity: 1, product: p }))
    : lines;
  const rawSubtotal = isBundle ? BUNDLE_PRICE : subtotal;
  const discountAmount = isBundle ? 0 : Math.round(subtotal * appliedPct * 100) / 100;
  const shipping = isBundle ? 0 : (subtotal >= SHIP_THRESHOLD ? 0 : SHIP_COST);
  const total = isBundle ? BUNDLE_PRICE : Math.max(0, subtotal - discountAmount) + shipping;

  const applyCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setApplyingCode(true);
    try {
      const r = await axios.get(`${API}/discount/validate`, { params: { code } });
      if (r.data.valid) {
        setAppliedPct(r.data.percent / 100);
        setDiscountCode(code);
        toast.success(`Codice ${code} applicato · -${r.data.percent}%`);
      } else {
        setAppliedPct(0);
        setDiscountCode('');
        toast.error('Codice sconto non valido');
      }
    } catch {
      toast.error('Impossibile verificare il codice');
    } finally {
      setApplyingCode(false);
    }
  };

  const removeCode = () => { setAppliedPct(0); setDiscountCode(''); setCodeInput(''); };


  const [f, setF] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    address: '', city: '', postal_code: '', province: '',
    is_business: false, vat_number: '',
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const pay = async (e) => {
    e.preventDefault();
    if (displayLines.length === 0) return toast.error('Carrello vuoto');
    if (f.is_business && !f.vat_number.trim()) return toast.error('Inserisci la Partita IVA');
    setLoading(true);
    try {
      await startCheckout(f, isBundle ? { items: displayLines, isBundle: true } : {});
    } catch {
      toast.error('Errore nel checkout. Riprova.');
      setLoading(false);
    }
  };

  if (isBundle && productsLoading && displayLines.length === 0) {
    return (
      <main data-testid="checkout-loading" className="container-essenya py-32 text-center">
        <div className="w-12 h-12 border-2 border-essenya-ink border-t-transparent rounded-full animate-spin mx-auto mb-6" />
        <p className="text-essenya-text">Preparo il tuo bundle…</p>
      </main>
    );
  }

  if (vacationMode) {
    return (
      <main data-testid="checkout-vacation" className="container-essenya py-28 text-center max-w-lg">
        <h1 className="font-display text-3xl md:text-4xl mb-4">Siamo in ferie</h1>
        <p className="text-essenya-text text-lg mb-2">Gli ordini sono momentaneamente sospesi.{returnDate ? <> Riapriamo il <strong>{returnDate}</strong>.</> : null}</p>
        <p className="text-essenya-text mb-8">Puoi comunque sfogliare i prodotti e salvare i tuoi preferiti per il rientro.</p>
        <Link to="/shop" className="btn-primary" data-testid="checkout-vacation-shop">Continua a sfogliare</Link>
      </main>
    );
  }

  if (displayLines.length === 0) {
    return <main className="container-essenya py-32 text-center"><p className="text-essenya-text">Carrello vuoto.</p><Link to="/shop" className="btn-primary mt-6">Inizia lo shopping</Link></main>;
  }

  const inputCls = "w-full bg-white border border-essenya-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-essenya-ink";

  return (
    <main data-testid="checkout-page" className="pt-10 pb-20">
      <div className="container-essenya max-w-5xl grid lg:grid-cols-[1.4fr_1fr] gap-10">
        {/* Form */}
        <form onSubmit={pay} className="order-2 lg:order-1">
          <h1 className="font-display text-3xl md:text-4xl mb-6">Dati di spedizione</h1>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-essenya-text mb-1.5">Nome *</label><input required value={f.first_name} onChange={set('first_name')} data-testid="co-first-name" className={inputCls} /></div>
            <div><label className="block text-xs text-essenya-text mb-1.5">Cognome *</label><input required value={f.last_name} onChange={set('last_name')} data-testid="co-last-name" className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs text-essenya-text mb-1.5">Email *</label><input required type="email" value={f.email} onChange={set('email')} data-testid="co-email" className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs text-essenya-text mb-1.5">Telefono</label><input value={f.phone} onChange={set('phone')} data-testid="co-phone" className={inputCls} /></div>
            <div className="col-span-2"><label className="block text-xs text-essenya-text mb-1.5">Indirizzo *</label><input required value={f.address} onChange={set('address')} data-testid="co-address" className={inputCls} placeholder="Via e numero civico" /></div>
            <div><label className="block text-xs text-essenya-text mb-1.5">Città *</label><input required value={f.city} onChange={set('city')} data-testid="co-city" className={inputCls} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs text-essenya-text mb-1.5">CAP *</label><input required value={f.postal_code} onChange={set('postal_code')} data-testid="co-cap" className={inputCls} /></div>
              <div><label className="block text-xs text-essenya-text mb-1.5">Prov.</label><input value={f.province} onChange={set('province')} data-testid="co-province" className={inputCls} maxLength={2} /></div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm mt-4 cursor-pointer">
            <input type="checkbox" checked={f.is_business} onChange={set('is_business')} data-testid="co-is-business" /> Acquisto come azienda (fattura con P.IVA)
          </label>
          {f.is_business && (
            <div className="mt-3"><label className="block text-xs text-essenya-text mb-1.5">Partita IVA *</label><input value={f.vat_number} onChange={set('vat_number')} data-testid="co-vat" className={inputCls} /></div>
          )}
          <button type="submit" disabled={loading} data-testid="checkout-submit" className="btn-primary w-full mt-6 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Reindirizzamento…' : `Vai al pagamento · ${fmt(total)}€`}
          </button>
          <p className="flex items-center gap-1.5 text-xs text-essenya-text justify-center mt-3"><Lock size={14} strokeWidth={1.5} /> Pagamento sicuro con Stripe (Carta · Apple Pay · Google Pay)</p>
        </form>

        {/* Summary */}
        <aside className="order-1 lg:order-2 bg-white rounded-2xl border border-essenya-border p-6 space-y-4 h-fit lg:sticky lg:top-24">
          <h2 className="font-display text-xl">Riepilogo ordine{isBundle && ' · Bundle'}</h2>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {displayLines.map(line => (
              <div key={line.product_id} className="flex gap-3" data-testid={`checkout-line-${line.product_id}`}>
                <img src={line.product.images[0]} alt="" className="w-14 h-16 rounded-md object-contain p-1 bg-essenya-surface2" />
                <div className="flex-1 text-sm">
                  <p className="font-medium">{line.product.name}</p>
                  <p className="text-essenya-text text-xs">Qtà: {line.quantity}</p>
                </div>
                <p className="text-sm font-display">{isBundle ? '—' : `${fmt(line.product.price * line.quantity)}€`}</p>
              </div>
            ))}
          </div>
          <div className="border-t border-essenya-border pt-3 space-y-2 text-sm">
            <div className="flex justify-between"><span>{isBundle ? 'Prezzo bundle' : 'Subtotale'}</span><span data-testid="checkout-subtotal">{fmt(rawSubtotal)}€</span></div>
            {!isBundle && appliedPct > 0 && (
              <div className="flex justify-between text-essenya-ink" data-testid="checkout-discount">
                <span className="inline-flex items-center gap-1"><Tag size={13} strokeWidth={1.5} />Sconto ({discountCode})</span>
                <span>-{fmt(discountAmount)}€</span>
              </div>
            )}
            <div className="flex justify-between"><span>Spedizione</span><span>{shipping === 0 ? 'Gratuita' : `${fmt(shipping)}€`}</span></div>
            <div className="flex justify-between font-display text-xl pt-2"><span>Totale</span><span data-testid="checkout-total">{fmt(total)}€</span></div>
          </div>
          {!isBundle && (
            <div className="pt-1">
              {appliedPct > 0 ? (
                <div className="flex items-center justify-between bg-essenya-surface2 border border-essenya-border rounded-lg px-3 py-2 text-sm" data-testid="checkout-applied-code">
                  <span className="inline-flex items-center gap-1.5 font-medium"><Check size={15} strokeWidth={2} className="text-green-600" />{discountCode} · -{Math.round(appliedPct * 100)}%</span>
                  <button type="button" onClick={removeCode} data-testid="remove-discount-btn" className="text-xs text-essenya-text underline hover:text-essenya-ink">Rimuovi</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={codeInput}
                    onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCode(); } }}
                    placeholder="Codice sconto"
                    data-testid="discount-code-input"
                    className="flex-1 bg-white border border-essenya-border rounded-lg px-3 py-2 text-sm outline-none focus:border-essenya-ink uppercase tracking-wide"
                  />
                  <button type="button" onClick={applyCode} disabled={applyingCode || !codeInput.trim()} data-testid="apply-discount-btn" className="btn-secondary px-4 text-sm disabled:opacity-50 inline-flex items-center gap-1.5">
                    {applyingCode && <Loader2 size={14} className="animate-spin" />}Applica
                  </button>
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 gap-2 pt-2 text-[11px] text-essenya-text">
            <span className="flex items-center gap-1"><ShieldCheck size={13} strokeWidth={1.5} />Sicuro</span>
            <span className="flex items-center gap-1"><Truck size={13} strokeWidth={1.5} />Tracciato</span>
            <span className="flex items-center gap-1"><RotateCcw size={13} strokeWidth={1.5} />Resi</span>
          </div>
        </aside>
      </div>
    </main>
  );
}
